import { createFileRoute } from "../lib/router-compat";
import { PageShell } from "../components/PageShell";

export const Route = createFileRoute("/sheet-music")({
  head: () => ({
    meta: [
      { title: "Steel Band Sheet Music — Carl & Nigel Chase" },
      {
        name: "description",
        content:
          "Sheet music written or arranged by Carl and Nigel Chase — over 400 songs for steel band, available at Bagaduce Music and direct from Nigel.",
      },
    ],
  }),
  component: SheetMusicPage,
});

const COLLECTIONS = [
  {
    label: "Carl & Susan Chase Collection",
    where: "Bagaduce Music",
    blurb: "The historic catalog — decades of arrangements, lovingly archived and distributed by Bagaduce Music Lending Library.",
    detail: "PDF · MP3 · scores & parts",
    cta: { label: "Browse the catalog", href: "https://www.bagaducemusic.org/collection/", external: true },
    accent: "from-primary/30 via-primary/10 to-transparent",
  },
  {
    label: "Nigel Chase Collection",
    where: "Direct from Nigel",
    blurb: "New and unpublished arrangements — written for the bands Nigel directs, sent personally on request.",
    detail: "Email for catalog & pricing",
    cta: { label: "Email Nigel", href: "mailto:nigelchase@gmail.com", external: false },
    accent: "from-amber-400/25 via-amber-400/5 to-transparent",
  },
];

const STATS = [
  { n: "400+", label: "Songs" },
  { n: "All", label: "Abilities" },
  { n: "2", label: "Collections" },
];

function SheetMusicPage() {
  return (
    <PageShell
      tag=""
      title={
        <>
          Sheet music by <span className="text-primary">Carl & Nigel Chase</span>.
        </>
      }
      intro="Sheet music written or arranged by Carl and Nigel Chase — for steel bands of every ability."
    >
      {/* Editorial hero */}
      <div className="relative">
        <div className="grid md:grid-cols-12 gap-8 md:gap-12 items-center">
          {/* Image — layered, offset */}
          <div className="md:col-span-5 relative">
            <div
              className="absolute -inset-6 rounded-[2rem] bg-primary/15 blur-3xl"
              aria-hidden
            />
            <div className="relative rounded-[1.75rem] overflow-hidden border border-border/40 shadow-2xl shadow-primary/10 rotate-[-2deg] hover:rotate-0 transition-transform duration-700">
              <img
                src="https://img1.wsimg.com/isteam/ip/cc067f05-785a-4de6-a400-eaada90fa319/unnamed-1-b99c3da.jpg/:/cr=t:17.53%25,l:17.53%25,w:64.94%25,h:64.94%25/rs=w:1600,m"
                alt="Steel band sheet music by Carl and Nigel Chase"
                className="w-full h-auto object-cover aspect-[4/5]"
                loading="lazy"
              />
            </div>
            <div
              className="absolute -bottom-6 -right-6 hidden md:block rotate-[6deg] rounded-2xl bg-card/80 backdrop-blur-md border border-border/40 px-5 py-4 shadow-xl"
            >
              <div className="text-[9px] uppercase tracking-[0.4em] text-primary">The catalog</div>
              <div className="font-display italic text-2xl mt-1">400+ songs</div>
            </div>
          </div>

          {/* Copy */}
          <div className="md:col-span-7">
            <div className="text-[10px] uppercase tracking-[0.5em] text-primary">For steel band</div>
            <h2 className="mt-4 font-display italic text-3xl md:text-6xl leading-[0.95]">
              Hammered out<br />
              <span className="text-primary">on the page.</span>
            </h2>
            <p className="mt-7 text-lg text-foreground/75 leading-relaxed max-w-xl">
              A wide variety of music arranged or composed for steel band, for
              players of every ability. PDFs, MP3s, scores and parts — written
              by hand, refined on the stand, ready to play.
            </p>

            <dl className="mt-10 grid grid-cols-3 max-w-md">
              {STATS.map((s, i) => (
                <div
                  key={s.label}
                  className={`px-4 ${i > 0 ? "border-l border-border/40" : ""}`}
                >
                  <dt className="font-display italic text-3xl md:text-4xl text-primary leading-none">
                    {s.n}
                  </dt>
                  <dd className="mt-2 text-[10px] uppercase tracking-[0.35em] text-foreground/55">
                    {s.label}
                  </dd>
                </div>
              ))}
            </dl>
          </div>
        </div>
      </div>

      {/* Collections */}
      <div className="mt-32">
        <div className="text-center mb-14">
          <div className="text-[10px] uppercase tracking-[0.5em] text-primary/80">
            Where to get it
          </div>
          <h2 className="mt-4 font-display italic text-3xl md:text-5xl">
            Two collections.
          </h2>
          <p className="mt-4 text-foreground/60 max-w-xl mx-auto">
            One archived, one alive — both available now.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {COLLECTIONS.map((c) => (
            <article
              key={c.label}
              className="group relative rounded-3xl border border-border/40 bg-card/30 backdrop-blur-md p-10 overflow-hidden hover:border-primary/50 transition-colors"
            >
              <div
                className={`absolute inset-0 bg-gradient-to-br ${c.accent} opacity-60 group-hover:opacity-100 transition-opacity`}
                aria-hidden
              />
              <div className="relative">
                <div className="text-[10px] uppercase tracking-[0.4em] text-primary mb-4">
                  {c.where}
                </div>
                <h3 className="font-display italic text-3xl md:text-4xl leading-tight">
                  {c.label}
                </h3>
                <p className="text-base text-foreground/70 mt-5 leading-relaxed">
                  {c.blurb}
                </p>
                <div className="mt-8 flex items-center justify-between gap-4 pt-6 border-t border-border/30">
                  <span className="text-[10px] uppercase tracking-[0.35em] text-foreground/50">
                    {c.detail}
                  </span>
                  <a
                    href={c.cta.href}
                    {...(c.cta.external
                      ? { target: "_blank", rel: "noreferrer" }
                      : {})}
                    className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary text-primary-foreground text-[11px] tracking-[0.3em] uppercase rounded-full hover:opacity-90 transition shadow-lg shadow-primary/20 whitespace-nowrap"
                  >
                    {c.cta.label} →
                  </a>
                </div>
              </div>
            </article>
          ))}
        </div>
      </div>
    </PageShell>
  );
}
